import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  ErrorHandler,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection
} from '@angular/core';
import { provideRouter, Router } from '@angular/router';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import * as Sentry from '@sentry/angular';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { firstValueFrom } from 'rxjs';

import { ApiModule, BASE_PATH, BookingService } from '../booking-api/client';
import { environment } from '../environments/environment';

import { routes } from './app.routes';
import { authInterceptor } from './auth.interceptor';
import { AuthService } from './auth.service';
import { ThemePreset } from './theme';

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: BASE_PATH, useValue: environment.apiBasePath },
    { provide: LOCALE_ID, useValue: 'de-DE' },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
    importProvidersFrom(ApiModule),
    providePrimeNG({
      theme: {
        //eslint-disable-next-line: @typescript-eslint/no-unsafe-assignment
        preset: ThemePreset,
        options: {
          darkModeSelector: '.app-dark'
        }
      }
    }),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptors([authInterceptor])),
    MessageService,
    provideTranslateService({
      fallbackLang: 'de',
      loader: provideTranslateHttpLoader({ prefix: './i18n/', suffix: `.json?v=${environment.buildVersion}` })
    }),
    provideAppInitializer(async () => {
      try {
        const bookingService = inject(BookingService);

        await firstValueFrom(bookingService.ping());
      } catch (e) {
        // @ts-expect-error error
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
        const error: string = e?.message?.toString();

        document.body.innerHTML = `
        <main class="grid min-h-full place-items-center bg-white px-6 py-24 sm:py-32 lg:px-8">
  <div class="text-center">
    <p class="text-base font-semibold text-indigo-600">500</p>
    <h1 class="mt-4 text-balance text-5xl font-semibold tracking-tight text-gray-900 sm:text-7xl">Ein kritischer Fehler ist aufgetreten</h1>
    <p class="mt-6 text-pretty text-lg font-medium text-gray-500 sm:text-xl/8 font-mono">${error}</p>
    <div class="mt-10 flex items-center justify-center gap-x-6">
      <a href="/" class="rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">Seite neu laden</a>
    </div>
  </div>
</main>`;

        console.log(e);
        throw e;
      }
    }),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler()
    },
    {
      provide: Sentry.TraceService,
      deps: [Router]
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    provideAppInitializer(async () => {
      const authService = inject(AuthService);
      await authService.init();
    })
  ]
};
